import "../../styles/mapAvatar.css";

import type { CSSProperties } from "react";
import type { UserProfile } from "../../types";
import {
  resolveMapAvatar,
  type ResolvedMapAvatar,
} from "../../utils/mapAvatarResolver";
import type { MapAvatarAssetDescriptor } from "../../constants/mapAvatarAssets";

type MapAvatarMarkerProps = {
  profile?: Partial<UserProfile> | null;
  profilePhotoUrl: string;
  isDark?: boolean;
  avatarScale?: number;
};

const MAP_AVATAR_VERSION = "v4180-slim-map-marker";
const BASE_ICON_SIZE = 94;
const BASE_ICON_ANCHOR_X = 47;
const BASE_ICON_ANCHOR_Y = 84;

const escapeHtmlAttr = (value: unknown) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/\"/g, "&quot;")
    .replace(/'/g, "&#39;");

const safeClassPart = (value?: string) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");

const clampAvatarScale = (scale?: number) => {
  const value = Number(scale);
  if (!Number.isFinite(value)) return 1;
  return Math.max(0.42, Math.min(1.12, value));
};

export const getMapAvatarScaleForZoom = (zoom?: number) => {
  const z = Number(zoom);
  if (!Number.isFinite(z)) return 1;
  if (z <= 7) return 0.62;
  if (z <= 8) return 0.68;
  if (z <= 9) return 0.76;
  if (z <= 10) return 0.86;
  return 1;
};

export const getMapAvatarIconMetrics = (avatarScale?: number) => ({
  scale: clampAvatarScale(avatarScale),
  iconSize: [BASE_ICON_SIZE, BASE_ICON_SIZE] as [number, number],
  iconAnchor: [BASE_ICON_ANCHOR_X, BASE_ICON_ANCHOR_Y] as [number, number],
});

export const getMapAvatarIconKey = (zoom?: number, loadoutKey?: string) =>
  `roddo-map-avatar:${Number.isFinite(Number(zoom)) ? Math.round(Number(zoom) * 10) / 10 : "z"}:${loadoutKey || "loadout"}`;

const scaleAttr = (avatarScale?: number) =>
  clampAvatarScale(avatarScale).toFixed(2);

const descriptorAccent = (
  item?: MapAvatarAssetDescriptor,
  fallback = "#67D8E8",
) => item?.accentColor || fallback;

const outfitDescriptor = (resolved: ResolvedMapAvatar) =>
  resolved.equipped.outerwear ||
  resolved.equipped.top ||
  resolved.equipped.pants ||
  resolved.equipped.footwear;

const gearDescriptor = (resolved: ResolvedMapAvatar) =>
  resolved.equipped.rodSkin || resolved.equipped.reelSkin;

const loadoutAccent = (resolved: ResolvedMapAvatar) =>
  descriptorAccent(
    resolved.equipped.background ||
      resolved.equipped.profileFrame ||
      resolved.equipped.outerwear ||
      resolved.equipped.top,
  );

const frameToneClass = (resolved: ResolvedMapAvatar) =>
  resolved.equipped.profileFrame
    ? `roddo-map-avatar--frame-${safeClassPart(resolved.profileFrameTone || "default")}`
    : "roddo-map-avatar--frame-none";

const cosmeticClass = (
  prefix: string,
  item?: MapAvatarAssetDescriptor,
) => (item?.cosmeticId ? `roddo-map-avatar--${prefix}-${safeClassPart(item.cosmeticId)}` : "");

const rootClasses = (resolved: ResolvedMapAvatar, isDark: boolean) =>
  [
    "roddo-map-avatar",
    isDark ? "roddo-map-avatar--dark" : "roddo-map-avatar--light",
    frameToneClass(resolved),
    resolved.equipped.background ? "roddo-map-avatar--has-background" : "",
    resolved.equipped.hat ? "roddo-map-avatar--has-hat" : "",
    outfitDescriptor(resolved) ? "roddo-map-avatar--has-outfit" : "",
    resolved.equipped.rodSkin ? "roddo-map-avatar--has-rod" : "",
    resolved.equipped.reelSkin ? "roddo-map-avatar--has-reel" : "",
    resolved.equipped.lureCharm ? "roddo-map-avatar--has-charm" : "",
    resolved.equipped.title ? "roddo-map-avatar--has-title" : "",
    cosmeticClass("hat", resolved.equipped.hat),
    cosmeticClass("top", resolved.equipped.top),
    cosmeticClass("outerwear", resolved.equipped.outerwear),
    cosmeticClass("pants", resolved.equipped.pants),
    cosmeticClass("footwear", resolved.equipped.footwear),
    cosmeticClass("rod", resolved.equipped.rodSkin),
    cosmeticClass("reel", resolved.equipped.reelSkin),
    cosmeticClass("charm", resolved.equipped.lureCharm),
    cosmeticClass("background", resolved.equipped.background),
    cosmeticClass("title", resolved.equipped.title),
  ]
    .filter(Boolean)
    .join(" ");

const rootVariables = (
  resolved: ResolvedMapAvatar,
  avatarScale?: number,
): CSSProperties => {
  const outfit = outfitDescriptor(resolved);
  const gear = gearDescriptor(resolved);
  return {
    "--roddo-map-avatar-scale": clampAvatarScale(avatarScale),
    "--roddo-avatar-loadout-accent": loadoutAccent(resolved),
    "--roddo-avatar-outfit-accent": descriptorAccent(outfit, "#4F7FC2"),
    "--roddo-avatar-gear-accent": descriptorAccent(gear, "#607D9A"),
    "--roddo-avatar-reel-accent": descriptorAccent(
      resolved.equipped.reelSkin,
      descriptorAccent(gear, "#607D9A"),
    ),
    "--roddo-avatar-charm-accent": descriptorAccent(
      resolved.equipped.lureCharm,
      "#4FAF9B",
    ),
    "--roddo-avatar-hat-accent": descriptorAccent(
      resolved.equipped.hat,
      "#5E8ED6",
    ),
    "--roddo-avatar-title-accent": descriptorAccent(
      resolved.equipped.title,
      "#67D8E8",
    ),
  } as CSSProperties;
};

const rootStyleHtml = (
  resolved: ResolvedMapAvatar,
  avatarScale?: number,
) => {
  const vars = rootVariables(resolved, avatarScale) as Record<string, string | number>;
  return ` style="${Object.entries(vars)
    .map(([key, value]) => `${key}:${escapeHtmlAttr(value)}`)
    .join(";")}" data-avatar-scale="${scaleAttr(avatarScale)}"`;
};

const prestigeAccentHtml = (resolved: ResolvedMapAvatar) => {
  const asset = resolved.profileFrameAccentAsset;
  if (!asset) return "";
  return `<span class="roddo-map-avatar__prestige" aria-hidden="true"><img src="${escapeHtmlAttr(asset)}" alt="" /></span>`;
};

const hatHtml = (resolved: ResolvedMapAvatar) => {
  const hat = resolved.equipped.hat;
  if (!hat) return "";
  return `<span class="roddo-map-avatar__hat" aria-hidden="true"><i></i><b></b></span>`;
};

const titleHtml = (resolved: ResolvedMapAvatar) => {
  const title = resolved.equipped.title;
  if (!title) return "";
  const token = String(title.glyph || title.name || "R")
    .replace(/[^A-Za-z0-9]/g, "")
    .slice(0, 2)
    .toUpperCase();
  return `<span class="roddo-map-avatar__title-tab" aria-hidden="true">${escapeHtmlAttr(token)}</span>`;
};

const markerInnerHtml = (
  resolved: ResolvedMapAvatar,
  profilePhotoUrl: string,
) => `
  <div class="roddo-map-avatar__shadow" aria-hidden="true"></div>
  <div class="roddo-map-avatar__halo" aria-hidden="true"></div>
  <div class="roddo-map-avatar__location-pulse" aria-hidden="true"></div>

  <span class="roddo-map-avatar__outfit" aria-hidden="true"><i></i><b></b><em></em></span>

  <div class="roddo-map-avatar__frame" aria-hidden="true">
    <div class="roddo-map-avatar__frame-ring"></div>
    <div class="roddo-map-avatar__frame-shine"></div>
  </div>

  <div class="roddo-map-avatar__profile-core">
    <div class="roddo-map-avatar__face-badge">
      <img src="${escapeHtmlAttr(profilePhotoUrl)}" alt="Your profile" />
    </div>
  </div>

  ${hatHtml(resolved)}
  <span class="roddo-map-avatar__rod" aria-hidden="true"><i></i><b></b></span>
  <span class="roddo-map-avatar__reel" aria-hidden="true"><i></i></span>
  <span class="roddo-map-avatar__charm" aria-hidden="true"><i></i><b></b></span>
  ${titleHtml(resolved)}
  ${prestigeAccentHtml(resolved)}
`;

export const renderResolvedMapAvatarMarkerHtml = ({
  resolved,
  profilePhotoUrl,
  isDark = false,
  avatarScale,
}: {
  resolved: ResolvedMapAvatar;
  profilePhotoUrl: string;
  isDark?: boolean;
  avatarScale?: number;
}) => `
  <div class="${rootClasses(resolved, isDark)}" data-avatar-version="${MAP_AVATAR_VERSION}"${rootStyleHtml(resolved, avatarScale)}>
    ${markerInnerHtml(resolved, profilePhotoUrl)}
  </div>
`;

export const renderMapAvatarMarkerHtml = ({
  profile,
  profilePhotoUrl,
  isDark = false,
  avatarScale,
}: MapAvatarMarkerProps) =>
  renderResolvedMapAvatarMarkerHtml({
    resolved: resolveMapAvatar(profile),
    profilePhotoUrl,
    isDark,
    avatarScale,
  });

export default function MapAvatarMarker({
  profile,
  profilePhotoUrl,
  isDark = false,
  avatarScale,
}: MapAvatarMarkerProps) {
  const resolved = resolveMapAvatar(profile);
  const title = resolved.equipped.title;
  const titleToken = String(title?.glyph || title?.name || "R")
    .replace(/[^A-Za-z0-9]/g, "")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className={rootClasses(resolved, isDark)}
      data-avatar-version={MAP_AVATAR_VERSION}
      data-avatar-scale={scaleAttr(avatarScale)}
      style={rootVariables(resolved, avatarScale)}
    >
      <div className="roddo-map-avatar__shadow" aria-hidden="true" />
      <div className="roddo-map-avatar__halo" aria-hidden="true" />
      <div className="roddo-map-avatar__location-pulse" aria-hidden="true" />

      <span className="roddo-map-avatar__outfit" aria-hidden="true">
        <i />
        <b />
        <em />
      </span>

      <div className="roddo-map-avatar__frame" aria-hidden="true">
        <div className="roddo-map-avatar__frame-ring" />
        <div className="roddo-map-avatar__frame-shine" />
      </div>

      <div className="roddo-map-avatar__profile-core">
        <div className="roddo-map-avatar__face-badge">
          <img src={profilePhotoUrl} alt="Your profile" />
        </div>
      </div>

      {resolved.equipped.hat ? (
        <span className="roddo-map-avatar__hat" aria-hidden="true">
          <i />
          <b />
        </span>
      ) : null}

      <span className="roddo-map-avatar__rod" aria-hidden="true">
        <i />
        <b />
      </span>
      <span className="roddo-map-avatar__reel" aria-hidden="true">
        <i />
      </span>
      <span className="roddo-map-avatar__charm" aria-hidden="true">
        <i />
        <b />
      </span>

      {title ? (
        <span className="roddo-map-avatar__title-tab" aria-hidden="true">
          {titleToken}
        </span>
      ) : null}

      {resolved.profileFrameAccentAsset ? (
        <span className="roddo-map-avatar__prestige" aria-hidden="true">
          <img src={resolved.profileFrameAccentAsset} alt="" />
        </span>
      ) : null}
    </div>
  );
}
